/* Card tinting: each product card derives its two slab hues from the
 * prominent vibrant colours of its own render.
 *
 * How: the image is drawn tiny on a canvas and every pixel votes for its
 * hue, weighted by saturation squared and a mid-lightness window, so a
 * magenta label outvotes a black bottle and a grey backdrop by orders of
 * magnitude. The strongest hue becomes --h1, the strongest sufficiently
 * distinct hue becomes --h2, and the slab's gradients, border, bloom and
 * hover glow all follow because they already read those variables.
 *
 * Fallbacks, in order: a card carrying data-tinted (server-side palette
 * from a metafield, when that lands) is left alone; an image that fails,
 * taints the canvas, or simply has no chroma keeps the aurora cycle.
 */
(function () {
  'use strict';

  var BINS = 36;            // 10 degree hue bins
  var SAMPLE = 40;          // canvas edge in px; 1600 votes per card
  var MIN_SCORE = 0.9;      // calibrated against real renders: vibrant labels
                            // score 3.5 to 10, sparse-but-real accents ~1,
                            // grey noise well under this
  var MIN_HUE_GAP = 50;     // degrees between the two slab hues

  function rgbToHsl(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    var max = Math.max(r, g, b);
    var min = Math.min(r, g, b);
    var l = (max + min) / 2;
    if (max === min) return [0, 0, l];
    var d = max - min;
    var s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    var h;
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0));
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    return [h * 60, s, l];
  }

  function extract(img) {
    var canvas = document.createElement('canvas');
    canvas.width = SAMPLE;
    canvas.height = SAMPLE;
    var ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, SAMPLE, SAMPLE);

    var data;
    try {
      data = ctx.getImageData(0, 0, SAMPLE, SAMPLE).data;
    } catch (e) {
      return null; // tainted canvas: no CORS on this image
    }

    var score = new Float32Array(BINS);
    var satSum = new Float32Array(BINS);
    var lumSum = 0;
    var lumCount = 0;

    for (var i = 0; i < data.length; i += 4) {
      if (data[i + 3] < 128) continue; // transparent pixels do not vote
      var hsl = rgbToHsl(data[i], data[i + 1], data[i + 2]);
      var s = hsl[1];
      var l = hsl[2];
      lumSum += l;
      lumCount++;
      if (s < 0.18) continue;
      // mid-lightness window: near-black and near-white carry no colour
      var window_ = l > 0.12 && l < 0.9 ? 1 - Math.abs(l - 0.5) : 0;
      if (!window_) continue;
      var w = s * s * window_;
      var bin = Math.floor(hsl[0] / (360 / BINS)) % BINS;
      score[bin] += w;
      satSum[bin] += s * w;
    }

    // neighbouring bins pool their votes, so a hue spread over 20 or 30
    // degrees (most printed labels) consolidates instead of splitting
    var smooth = new Float32Array(BINS);
    for (var sb = 0; sb < BINS; sb++) {
      smooth[sb] = score[(sb + BINS - 1) % BINS] + score[sb] + score[(sb + 1) % BINS];
    }

    var best = 0;
    for (var b = 1; b < BINS; b++) if (smooth[b] > smooth[best]) best = b;
    if (smooth[best] < MIN_SCORE) {
      // colourless render: no hues, but the brightness verdict still counts
      return { hues: null, brightness: lumCount ? lumSum / lumCount : 0 };
    }

    // second hue: strongest bin far enough around the wheel from the first
    var second = -1;
    for (var b2 = 0; b2 < BINS; b2++) {
      var gap = Math.abs(b2 - best) * (360 / BINS);
      gap = Math.min(gap, 360 - gap);
      if (gap < MIN_HUE_GAP) continue;
      if (second === -1 || smooth[b2] > smooth[second]) second = b2;
    }

    var binHue = function (b3) { return (b3 + 0.5) * (360 / BINS); };
    var binSat = function (b4) {
      var s2 = score[b4] > 0 ? satSum[b4] / score[b4] : 0.7;
      return Math.min(0.95, Math.max(0.6, s2 * 1.15));
    };

    var h1 = 'hsl(' + Math.round(binHue(best)) + ' ' + Math.round(binSat(best) * 100) + '% 56%)';
    var h2;
    if (second !== -1 && smooth[second] > smooth[best] * 0.12) {
      h2 = 'hsl(' + Math.round(binHue(second)) + ' ' + Math.round(binSat(second) * 100) + '% 56%)';
    } else {
      h2 = 'hsl(' + Math.round((binHue(best) + 55) % 360) + ' ' + Math.round(binSat(best) * 100) + '% 52%)';
    }
    return { hues: [h1, h2], brightness: lumCount ? lumSum / lumCount : 0 };
  }

  function tint(card) {
    if (card.dataset.scanned || card.dataset.tinted) return;
    card.dataset.scanned = '1';
    var img = card.querySelector('.ak-pcard__stage img');
    if (!img) return;

    var run = function () {
      if (!img.naturalWidth) return;
      var result = extract(img);
      if (!result) return;
      // Renders shot on light studio grounds blow holes in the dark grid;
      // the slab absorbs them under a hue-tinted veil instead.
      if (result.brightness > 0.52) card.classList.add('is-bright');
      if (result.hues) {
        card.style.setProperty('--h1', result.hues[0]);
        card.style.setProperty('--h2', result.hues[1]);
        card.dataset.tinted = 'image';
        feedHero(result.hues[0]);
      }
    };

    if (img.complete) {
      run();
    } else {
      img.addEventListener('load', run, { once: true });
    }
  }

  /* The range hero drinks from the bench: the first two distinct card hues
     on the page become the hero's aurora, so every collection carries the
     atmosphere of its own products. */
  var feedHero = (function () {
    var hero = null;
    var fed = [];
    return function (h1) {
      if (hero === null) hero = document.querySelector('[data-ak-range-hero]') || false;
      if (!hero) return;
      if (fed.indexOf(h1) !== -1) return;
      fed.push(h1);
      if (fed.length === 1) {
        hero.style.setProperty('--h1', fed[0]);
      } else if (fed.length === 2) {
        hero.style.setProperty('--h2', fed[1]);
      }
    };
  })();

  function boot() {
    var cards = document.querySelectorAll('.ak-pcard');
    var i = 0;
    var step = function (deadline) {
      while (i < cards.length && (!deadline || deadline.timeRemaining() > 3)) {
        tint(cards[i++]);
      }
      if (i < cards.length) idle(step);
    };
    var idle = window.requestIdleCallback || function (fn) { window.setTimeout(fn, 40); };
    idle(step);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // infinite scroll appends fresh cards; scan the newcomers
  document.addEventListener('ak:cards:added', boot);
})();
